/**
 * A JSON-safe value that can be serialised and sent across the wire.
 * Excludes non-transportable JS values such as Date, BigInt, and functions.
 */
type JsonValue =
  | null
  | string
  | number
  | boolean
  | JsonValue[]
  | { [key: string]: JsonValue };

export type { JsonValue };

export interface PopupCustomTextScope {
  type: "proposalPlan";
  rfpId: string;
  proposalPlanId: string;
}

export interface PopupCustomTextFieldIdentity {
  scope: PopupCustomTextScope;
  coverageType: string;
  benefitPlanId: string;
  benefitKey: string;
  propertyKey?: string;
  network?: string;
  overrideType?: typeof POPUP_CUSTOM_TEXT_OVERRIDE_TYPE;
}

export interface PopupCustomTextValue {
  customText: string | null;
  originalValue?: JsonValue;
}

export interface PopupCustomTextRecord
  extends PopupCustomTextFieldIdentity, PopupCustomTextValue {
  updatedAt: string;
  updatedBy?: string;
}

export interface PopupCustomTextUpsertRequest
  extends PopupCustomTextFieldIdentity, PopupCustomTextValue {}

export type PopupCustomTextByFieldKey = ReadonlyMap<
  string,
  PopupCustomTextRecord
>;

export interface PopupCustomTextListResponse {
  rfpId: string;
  overrides: PopupCustomTextRecord[];
}

export interface PopupCustomTextUpsertBody {
  overrides: PopupCustomTextUpsertRequest[];
}

export const POPUP_CUSTOM_TEXT_OVERRIDE_TYPE = "custom_text" as const;

const FIELD_KEY_PART_SEPARATOR = "::";
const EMPTY_FIELD_KEY_PART = "-";

export const normalizeFieldKeyPart = (value: string | undefined): string =>
  value && value.trim() ? value.trim() : EMPTY_FIELD_KEY_PART;

const scopeKeyParts = (scope: PopupCustomTextScope): string[] => [
  scope.rfpId,
  scope.type,
  scope.proposalPlanId,
];

export const buildPopupCustomTextFieldKey = (
  identity: PopupCustomTextFieldIdentity,
): string =>
  [
    ...scopeKeyParts(identity.scope),
    identity.coverageType,
    identity.benefitPlanId,
    identity.benefitKey,
    identity.propertyKey,
    identity.network,
  ]
    .map(normalizeFieldKeyPart)
    .join(FIELD_KEY_PART_SEPARATOR);

export const buildPopupCustomTextCellKey = (
  identity: PopupCustomTextFieldIdentity,
): string => {
  const columnField = identity.network
    ? `${identity.benefitPlanId}.${identity.network}`
    : identity.benefitPlanId;

  return [columnField, identity.benefitKey, identity.propertyKey]
    .map(normalizeFieldKeyPart)
    .join(FIELD_KEY_PART_SEPARATOR);
};

export const parsePopupCustomTextCellKey = (
  cellKey: string,
):
  | Pick<
      PopupCustomTextFieldIdentity,
      "benefitPlanId" | "benefitKey" | "propertyKey" | "network"
    >
  | undefined => {
  const [columnField, benefitKey, propertyKey] = cellKey.split(
    FIELD_KEY_PART_SEPARATOR,
  );
  if (!columnField || !benefitKey) return undefined;

  const [benefitPlanId, ...networkParts] = columnField.split(".");
  if (!benefitPlanId) return undefined;

  const network = networkParts.join(".");

  return {
    benefitPlanId,
    benefitKey,
    propertyKey:
      propertyKey && propertyKey !== EMPTY_FIELD_KEY_PART
        ? propertyKey
        : undefined,
    network: network || undefined,
  };
};

export const indexPopupCustomTextRecords = (
  records: PopupCustomTextRecord[],
): PopupCustomTextByFieldKey =>
  new Map(
    records.map((record) => [buildPopupCustomTextFieldKey(record), record]),
  );

export const findPopupCustomTextRecord = (
  recordsByFieldKey: PopupCustomTextByFieldKey,
  identity: PopupCustomTextFieldIdentity,
): PopupCustomTextRecord | undefined =>
  recordsByFieldKey.get(buildPopupCustomTextFieldKey(identity));

export const buildPopupCustomTextUpsertRequest = (
  identity: PopupCustomTextFieldIdentity,
  value: PopupCustomTextValue,
): PopupCustomTextUpsertRequest => ({
  ...identity,
  customText: value.customText,
  originalValue: value.originalValue,
});
